import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { PiClipboardTextBold, PiCubeBold, PiSquaresFourBold } from 'react-icons/pi';
import { Text, LoadingState } from '@v2/design-system';
import api from '@v2/lib/api';
import ConferenceSheetImportButton from '@v2/features/shipment-tasks/ConferenceSheetImportButton';

export type CheckItemRow = {
    id: number;
    client: string | null;
    item_code: string | null;
    product_name: string | null;
    qty_expected: number;
    packages_expected: number;
    frame_color: string | null;
    upholstery_color: string | null;
    stitches_color: string | null;
    arm_color: string | null;
    box_numbers: string | null;
    box_numbers_display: string | null;
    net_weight_kg: number | null;
    weight_tower_qty: number | null;
    total_net_weight_kg: number | null;
    product_image_url: string | null;
    check_qty: string;
    check_product: string;
    check_color: string;
    check_packaging: string;
};

export type CheckContainerRow = {
    id: number;
    container_number: string | null;
    invoice_number: string | null;
    file_name: string | null;
    created_at: string | null;
    download_url: string;
    status: string;
    totals: { items: number; ok: number; issue: number; pending: number };
    package_rows: Array<{
        row_key: string;
        check_item_id: number;
        package_index: number;
        package_total: number;
        item_code: string | null;
        product_name: string | null;
        box_label: string;
        validated: boolean;
    }>;
    items: CheckItemRow[];
};

type CheckResponse = {
    shipment: { id: number; order_number: string };
    containers: CheckContainerRow[];
    edit_v1_url: string;
};

type ViewMode = 'cards' | 'table';

function statusTone(field: string): 'ok' | 'nok' | 'pending' {
    if (field === 'ok') return 'ok';
    if (field === 'nok') return 'nok';
    return 'pending';
}

function StatusDot({ value, label }: { value: string; label: string }) {
    const tone = statusTone(value);
    const cls =
        tone === 'ok'
            ? 'bg-emerald-100 text-emerald-800'
            : tone === 'nok'
              ? 'bg-red-100 text-red-800'
              : 'bg-gray-100 text-gray-600';
    return (
        <span className={`rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase ${cls}`} title={label}>
            {label.slice(0, 3)}
        </span>
    );
}

function colorsSummary(item: CheckItemRow): string {
    return [item.frame_color, item.upholstery_color, item.stitches_color, item.arm_color]
        .filter(Boolean)
        .join(' · ');
}

function ColorChip({ label, value }: { label: string; value: string | null }) {
    if (!value) return null;
    return (
        <span
            className="inline-block max-w-full truncate rounded bg-gray-100 px-1.5 py-0.5 text-[10px] text-gray-700"
            title={`${label}: ${value}`}
        >
            <span className="font-medium text-gray-500">{label}: </span>
            {value}
        </span>
    );
}

function ItemCard({ item }: { item: CheckItemRow }) {
    return (
        <li className="flex h-full flex-col rounded-lg border border-gray-100 bg-gray-50/80 p-2.5">
            {item.product_image_url ? (
                <img
                    src={item.product_image_url}
                    alt=""
                    className="mb-2 aspect-[4/3] w-full rounded-md border border-gray-200 bg-white object-cover"
                />
            ) : (
                <div className="mb-2 flex aspect-[4/3] w-full items-center justify-center rounded-md border border-dashed border-gray-200 bg-white text-xs text-gray-400">
                    SKU
                </div>
            )}
            <p className="font-mono text-xs font-bold leading-tight text-gray-900">{item.item_code || '—'}</p>
            <p className="mt-0.5 line-clamp-2 text-xs leading-snug text-gray-600">{item.product_name || '—'}</p>
            <p className="mt-1 text-xs font-semibold text-gray-800">
                {item.qty_expected} un · {item.packages_expected} cx
            </p>
            <div className="mt-1.5 flex flex-wrap gap-1">
                <ColorChip label="Estr" value={item.frame_color} />
                <ColorChip label="Est" value={item.upholstery_color} />
                <ColorChip label="Cos" value={item.stitches_color} />
                <ColorChip label="Bra" value={item.arm_color} />
            </div>
            <p className="mt-1 text-[10px] text-gray-600">
                Caixas: <span className="font-medium">{item.box_numbers_display ?? '—'}</span>
                {(item.weight_tower_qty ?? 0) > 0 ? (
                    <span className="ms-2">· Torres: {item.weight_tower_qty}</span>
                ) : null}
                {item.total_net_weight_kg != null ? (
                    <span className="ms-2">
                        · {item.total_net_weight_kg.toLocaleString('pt-BR', { maximumFractionDigits: 2 })} kg
                    </span>
                ) : null}
            </p>
            <div className="mt-auto flex flex-wrap gap-1 pt-1.5">
                <StatusDot value={item.check_qty} label="Qtd" />
                <StatusDot value={item.check_product} label="Prod" />
                <StatusDot value={item.check_color} label="Cor" />
                <StatusDot value={item.check_packaging} label="Emb" />
            </div>
        </li>
    );
}

function ContainerItemsTable({ items }: { items: CheckItemRow[] }) {
    return (
        <div className="overflow-x-auto">
            <table className="min-w-full text-xs">
                <thead className="bg-gray-50 text-gray-600">
                    <tr>
                        <th className="px-2 py-2 text-left font-semibold">SKU</th>
                        <th className="px-2 py-2 text-left font-semibold">Produto</th>
                        <th className="px-2 py-2 text-right font-semibold">Qtd</th>
                        <th className="px-2 py-2 text-right font-semibold">Cx</th>
                        <th className="px-2 py-2 text-left font-semibold">Cores</th>
                        <th className="px-2 py-2 text-center font-semibold">Conf.</th>
                    </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                    {items.map((item) => (
                        <tr key={item.id} className="hover:bg-gray-50/80">
                            <td className="whitespace-nowrap px-2 py-2 font-mono font-semibold text-gray-900">
                                {item.item_code ?? '—'}
                            </td>
                            <td className="max-w-[180px] px-2 py-2">
                                <p className="line-clamp-2 text-gray-700">{item.product_name ?? '—'}</p>
                            </td>
                            <td className="px-2 py-2 text-right tabular-nums">{item.qty_expected}</td>
                            <td className="px-2 py-2 text-right tabular-nums">{item.packages_expected}</td>
                            <td className="max-w-[140px] truncate px-2 py-2 text-gray-600" title={colorsSummary(item)}>
                                {colorsSummary(item) || '—'}
                            </td>
                            <td className="px-2 py-2">
                                <div className="flex flex-wrap justify-center gap-0.5">
                                    <StatusDot value={item.check_qty} label="Qtd" />
                                    <StatusDot value={item.check_product} label="Prod" />
                                    <StatusDot value={item.check_color} label="Cor" />
                                    <StatusDot value={item.check_packaging} label="Emb" />
                                </div>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

function ContainerColumn({ container, viewMode }: { container: CheckContainerRow; viewMode: ViewMode }) {
    const byClient = useMemo(() => {
        const map = new Map<string, CheckItemRow[]>();
        for (const item of container.items) {
            const key = item.client?.trim() || '—';
            if (!map.has(key)) map.set(key, []);
            map.get(key)!.push(item);
        }
        return Array.from(map.entries());
    }, [container.items]);

    return (
        <div className="flex h-full min-h-[320px] w-full flex-col rounded-xl border border-gray-200 bg-white shadow-sm">
            <div className="border-b border-gray-100 bg-gradient-to-r from-amber-50 to-white px-4 py-3">
                <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                        <div className="flex items-center gap-1.5 text-sm font-bold text-gray-900">
                            <PiCubeBold className="size-4 shrink-0 text-amber-600" />
                            <span className="truncate">{container.container_number || `Check #${container.id}`}</span>
                        </div>
                        {container.invoice_number ? (
                            <p className="mt-0.5 truncate text-xs text-gray-500">{container.invoice_number}</p>
                        ) : null}
                    </div>
                    <a
                        href={container.download_url}
                        target="_blank"
                        rel="noreferrer"
                        className="shrink-0 text-[10px] text-primary hover:underline"
                    >
                        Excel
                    </a>
                </div>
                <div className="mt-2 flex flex-wrap gap-1.5 text-center text-[10px]">
                    <span className="rounded bg-gray-100 px-2 py-0.5 font-semibold text-gray-700">
                        {container.totals.items} itens
                    </span>
                    <span className="rounded bg-emerald-50 px-2 py-0.5 font-semibold text-emerald-700">
                        {container.totals.ok} OK
                    </span>
                    <span className="rounded bg-amber-50 px-2 py-0.5 font-semibold text-amber-700">
                        {container.totals.pending} pend.
                    </span>
                    {container.totals.issue > 0 ? (
                        <span className="rounded bg-red-50 px-2 py-0.5 font-semibold text-red-700">
                            {container.totals.issue} NOK
                        </span>
                    ) : null}
                </div>
            </div>

            <div className="max-h-[calc(100vh-280px)] flex-1 overflow-y-auto p-3 md:p-4">
                <div className="mb-4 rounded-lg border border-gray-100 bg-gray-50/80 p-3">
                    <p className="mb-2 text-xs font-bold uppercase tracking-wide text-gray-600">
                        Caixas do container ({container.package_rows.length})
                    </p>
                    {container.package_rows.length === 0 ? (
                        <p className="text-xs text-gray-500">Nenhuma caixa encontrada para este container.</p>
                    ) : (
                        <div className="flex flex-wrap gap-2">
                            {container.package_rows.map((row) => (
                                <span
                                    key={row.row_key}
                                    className={`rounded-full px-2.5 py-1 text-[11px] font-semibold ${
                                        row.validated
                                            ? 'bg-emerald-100 text-emerald-800 ring-1 ring-emerald-300'
                                            : 'bg-gray-100 text-gray-700 ring-1 ring-gray-200'
                                    }`}
                                    title={`${row.item_code ?? 'Item'} · ${row.product_name ?? 'Produto'}`}
                                >
                                    {row.item_code ?? 'Item'} · cx {row.box_label}
                                </span>
                            ))}
                        </div>
                    )}
                </div>

                {byClient.map(([client, items]) => (
                    <div key={client} className="mb-4 last:mb-0">
                        <p className="sticky top-0 z-[1] mb-2 border-b border-amber-100 bg-white/95 py-1 text-xs font-bold uppercase tracking-wide text-amber-800">
                            {client}
                            <span className="ms-1 font-normal text-gray-500">({items.length})</span>
                        </p>
                        {viewMode === 'cards' ? (
                            <ul className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                                {items.map((item) => (
                                    <ItemCard key={item.id} item={item} />
                                ))}
                            </ul>
                        ) : (
                            <ContainerItemsTable items={items} />
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
}

function ViewToggle({ mode, onChange }: { mode: ViewMode; onChange: (m: ViewMode) => void }) {
    const btn = (active: boolean) =>
        active
            ? 'bg-white text-gray-900 shadow-sm'
            : 'text-gray-600 hover:text-gray-900';

    return (
        <div className="inline-flex rounded-lg border border-gray-200 bg-gray-100 p-0.5 text-xs font-medium">
            <button
                type="button"
                className={`inline-flex items-center gap-1 rounded-md px-3 py-1.5 transition ${btn(mode === 'cards')}`}
                onClick={() => onChange('cards')}
            >
                <PiSquaresFourBold className="size-3.5" />
                Cards (2 por linha)
            </button>
            <button
                type="button"
                className={`inline-flex items-center gap-1 rounded-md px-3 py-1.5 transition ${btn(mode === 'table')}`}
                onClick={() => onChange('table')}
            >
                <PiClipboardTextBold className="size-3.5" />
                Tabela
            </button>
        </div>
    );
}

export default function ShipmentCheckByContainerView({ shipmentId }: { shipmentId: string }) {
    const [viewMode, setViewMode] = useState<ViewMode>('cards');

    const { data, isLoading, refetch } = useQuery({
        queryKey: ['shipment-check', shipmentId],
        queryFn: async () => {
            const { data } = await api.get<CheckResponse>(`/shipments/${shipmentId}/check`);
            return data;
        },
    });

    if (isLoading) return <LoadingState variant="detail" />;
    if (!data) return null;

    if (data.containers.length === 0) {
        return (
            <div className="rounded-xl border border-gray-200 bg-white p-6 text-sm text-gray-600 shadow-sm">
                <p>Nenhum container importado. Use a planilha Details na tarefa de embarque ou abaixo.</p>
                <div className="mt-3">
                    <ConferenceSheetImportButton shipmentId={Number(shipmentId)} onSuccess={() => refetch()} />
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-3">
            <div className="flex flex-wrap items-center justify-between gap-3">
                <p className="text-xs text-gray-500">
                    {data.containers.length} container(s) · conferência física (OK/NOK) no{' '}
                    <a href={data.edit_v1_url} className="text-primary hover:underline">
                        painel v1
                    </a>
                </p>
                <ViewToggle mode={viewMode} onChange={setViewMode} />
            </div>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                {data.containers.map((c) => (
                    <ContainerColumn key={c.id} container={c} viewMode={viewMode} />
                ))}
            </div>
        </div>
    );
}
